import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { auth, db } from "../../firebase";
import { cotizacionRapida } from "../../utils/presupuestoCalc";
import { colors, radius, shadows } from "../../utils/designTokens";
import PresupuestoExpressTemplate from "../../components/presupuesto/PresupuestoExpressTemplate";
import { useCotizadorPrefill } from "../../context/CotizadorContext";
import isoLogo from "../../assets/habitia-iso.png";
import { FaEnvelopeOpenText, FaFilePdf } from "react-icons/fa";

/**
 * Presupuesto exprés — herramienta APARTE del Cotizador de 6 páginas.
 * Formulario corto → PDF de 1 hoja A4 personalizado para dejar en el edificio
 * (cartas / sobre para la comisión). Reusa la tarifa (cotizacionRapida) y el
 * contacto del comercial (nunca el de los founders — lead propio).
 */

const fmt = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-UY");
const soloDigitos = (s) => String(s || "").replace(/\D/g, "");
// Normaliza a wa.me (Uruguay): agrega 598 y saca el 0 inicial del celular local.
const toWa = (s) => {
  let d = soloDigitos(s);
  if (d.startsWith("598")) return d;
  if (d.startsWith("0")) d = d.slice(1);
  return d ? "598" + d : "";
};
const MENSAJE_QR = "Hola, vimos la propuesta de Habitia para nuestro edificio y queremos coordinar una reunión.";

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// QR (alta corrección) con el isotipo Habitia al centro → dataURL (para el template).
async function makeQrDataUrl(waDigits) {
  const url = `https://wa.me/${waDigits}?text=${encodeURIComponent(MENSAJE_QR)}`;
  const size = 600;
  const qrUrl = await QRCode.toDataURL(url, {
    errorCorrectionLevel: "H", margin: 1, width: size,
    color: { dark: "#0b2240", light: "#ffffff" },
  });
  return await new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    const qrImg = new Image();
    qrImg.onload = () => {
      ctx.drawImage(qrImg, 0, 0, size, size);
      const logo = new Image();
      logo.onload = () => {
        const ls = size * 0.2, pad = ls * 1.4, c = (size - pad) / 2;
        ctx.fillStyle = "#fff";
        roundRect(ctx, c, c, pad, pad, pad * 0.22); ctx.fill();
        ctx.drawImage(logo, (size - ls) / 2, (size - ls) / 2, ls, ls);
        resolve(canvas.toDataURL("image/png"));
      };
      logo.onerror = () => resolve(qrUrl);
      logo.src = isoLogo;
    };
    qrImg.onerror = () => resolve(qrUrl);
    qrImg.src = qrUrl;
  });
}

export default function PresupuestoExpress() {
  // Prefill desde la ficha del CRM/mapa ("Presupuesto exprés"): datos del catastro.
  const { prefill, clearPrefill } = useCotizadorPrefill();
  // Defaults "para quedar cubiertos" (pedido): 1 ascensor, 1 amenity, 1 empleado (portero).
  const [f, setF] = useState(() => ({
    edificio: prefill?.edificio || "",
    direccion: prefill?.direccion || "",
    barrio: prefill?.barrio || "",
    apartamentos: prefill ? (Number(prefill.apartamentos) || 0) : 0,
    porteros: 1, limpiadoras: 0, ascensores: 1, amenities: 1,
    antiguedadAnios: prefill ? (Number(prefill.antiguedadAnios) || 0) : 0,
  }));
  const [comercial, setComercial] = useState("");
  const [wa, setWa] = useState("");
  const [email, setEmail] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const templateRef = useRef(null);
  // Vista previa: escala para que TODA la hoja A4 entre en el recuadro.
  const previewBoxRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.5);

  const setNum = (k, v) => setF((p) => ({ ...p, [k]: Math.max(0, Math.floor(Number(v) || 0)) }));
  const setTxt = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Consumir el prefill una sola vez (que no quede pegado al re-navegar).
  useEffect(() => { if (prefill) clearPrefill(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill nombre + celular + email del comercial logueado (TODO editable — lead
  // propio; el email es un default que el comercial puede cambiar por otro).
  useEffect(() => {
    (async () => {
      const authEmail = (auth.currentUser?.email || "").toLowerCase();
      if (!authEmail) return;
      setEmail(authEmail); // default; lo puede editar
      try {
        const snap = await getDocs(query(collection(db, "users"), where("email", "==", authEmail)));
        let best = null;
        snap.forEach((d) => { const u = d.data(); if (!best || u.celular) best = u; });
        if (best) {
          setComercial(best.NombreCompleto || best.name || "");
          if (best.celular) setWa(best.celular);
          if (best.email) setEmail(best.email);
        }
      } catch (e) { /* editable igual */ }
    })();
  }, []);

  // Escala responsive: que la hoja A4 (210×297mm ≈ 794×1123px) entre COMPLETA
  // en el recuadro de la vista previa (fit por ancho y alto).
  useEffect(() => {
    const A4_W = 794, A4_H = 1123, PAD = 24;
    const el = previewBoxRef.current;
    if (!el) return;
    const recompute = () => {
      const w = el.clientWidth - PAD;
      const h = el.clientHeight - PAD;
      if (w <= 0 || h <= 0) return;
      setPreviewScale(Math.max(0.1, Math.min(w / A4_W, h / A4_H)));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Regenerar el QR cuando cambia el WhatsApp.
  useEffect(() => {
    const d = toWa(wa);
    if (d.length < 9) { setQrDataUrl(""); return; }
    let alive = true;
    makeQrDataUrl(d).then((u) => { if (alive) setQrDataUrl(u); });
    return () => { alive = false; };
  }, [wa]);

  const q = useMemo(() => cotizacionRapida({
    apartamentos: f.apartamentos, porteros: f.porteros, limpiadoras: f.limpiadoras,
    ascensores: f.ascensores, amenities: f.amenities, antiguedadAnios: f.antiguedadAnios,
  }), [f]);

  const chips = useMemo(() => {
    const out = [];
    if (f.apartamentos > 0) out.push(`${f.apartamentos} unidades`);
    if (f.porteros > 0) out.push("Con portero");
    if (f.ascensores > 0) out.push(f.ascensores > 1 ? `${f.ascensores} ascensores` : "Con ascensor");
    if (f.barrio.trim()) out.push(f.barrio.trim());
    return out;
  }, [f]);

  const templateData = useMemo(() => ({
    edificio: f.edificio.trim() || "Su edificio",
    direccion: f.direccion.trim(),
    chips,
    honorario: q.totalConIva > 0 ? fmt(q.totalConIva) : "—",
    porUnidad: f.apartamentos > 0 ? `≈ ${fmt(q.totalConIva / f.apartamentos)} por unidad` : "",
    comercial: comercial.trim(),
    wa: wa.trim(),
    email: email.trim(),
    qrDataUrl,
    nro: `${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
    fecha: new Date().toLocaleDateString("es-UY", { day: "numeric", month: "long", year: "numeric" }),
  }), [f, q, chips, comercial, wa, email, qrDataUrl]);

  const handleGenerar = async () => {
    setError("");
    if (!f.edificio.trim()) { setError("Poné el nombre del edificio."); return; }
    if (f.apartamentos <= 0) { setError("Ingresá la cantidad de unidades."); return; }
    if (!wa.trim()) { setError("Falta tu WhatsApp de contacto."); return; }
    setGenerating(true);
    try {
      // Asegurar que el template (offscreen) esté renderizado con el QR actual.
      await new Promise((r) => setTimeout(r, 150));
      const canvas = await html2canvas(templateRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, w, h);
      const safe = (f.edificio.trim() || "edificio").replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
      pdf.save(`Presupuesto-Habitia-${safe}.pdf`);
    } catch (e) {
      console.error("Error generando el presupuesto exprés:", e);
      setError("No se pudo generar el PDF. Probá de nuevo.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <FaEnvelopeOpenText size={20} color={colors.primary} />
        <div>
          <h1 style={S.title}>Presupuesto exprés</h1>
          <p style={S.subtitle}>Una hoja personalizada para imprimir y dejar en el edificio. El Cotizador completo de 6 páginas sigue igual.</p>
        </div>
      </div>

      <div style={S.grid}>
        {/* FORM */}
        <div style={S.card}>
          <div style={S.section}>Edificio</div>
          <Field label="Nombre del edificio *"><input style={S.input} value={f.edificio} onChange={(e) => setTxt("edificio", e.target.value)} placeholder="Ej: Torre Rambla Sur" /></Field>
          <Field label="Dirección"><input style={S.input} value={f.direccion} onChange={(e) => setTxt("direccion", e.target.value)} placeholder="Calle 1234" /></Field>
          <Field label="Barrio"><input style={S.input} value={f.barrio} onChange={(e) => setTxt("barrio", e.target.value)} placeholder="Pocitos" /></Field>

          <div style={S.section}>Datos para el precio</div>
          <div style={S.row}>
            <Field label="Unidades *"><input type="number" min="0" style={S.input} value={f.apartamentos || ""} onChange={(e) => setNum("apartamentos", e.target.value)} /></Field>
            <Field label="Antigüedad (años)"><input type="number" min="0" style={S.input} value={f.antiguedadAnios || ""} onChange={(e) => setNum("antiguedadAnios", e.target.value)} /></Field>
          </div>
          <div style={S.row}>
            <Field label="Porteros"><input type="number" min="0" style={S.input} value={f.porteros || ""} onChange={(e) => setNum("porteros", e.target.value)} /></Field>
            <Field label="Personal limpieza"><input type="number" min="0" style={S.input} value={f.limpiadoras || ""} onChange={(e) => setNum("limpiadoras", e.target.value)} /></Field>
          </div>
          <div style={S.row}>
            <Field label="Ascensores"><input type="number" min="0" style={S.input} value={f.ascensores || ""} onChange={(e) => setNum("ascensores", e.target.value)} /></Field>
            <Field label="Amenities"><input type="number" min="0" style={S.input} value={f.amenities || ""} onChange={(e) => setNum("amenities", e.target.value)} /></Field>
          </div>

          <div style={S.section}>Tu contacto (va en la hoja + el QR)</div>
          <Field label="Tu nombre"><input style={S.input} value={comercial} onChange={(e) => setComercial(e.target.value)} placeholder="Nombre del comercial" /></Field>
          <Field label="Tu WhatsApp *"><input style={S.input} value={wa} onChange={(e) => setWa(e.target.value)} placeholder="099 123 456" /></Field>
          <Field label="Tu email"><input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vos@correo.com" /></Field>

          {error ? <div style={S.error}>{error}</div> : null}
          <button type="button" style={{ ...S.btn, opacity: generating ? 0.7 : 1 }} onClick={handleGenerar} disabled={generating}>
            <FaFilePdf /> {generating ? "Generando…" : "Generar hoja (PDF)"}
          </button>
        </div>

        {/* PREVIEW en vivo (mismo template que el PDF, escalado) */}
        <div style={S.previewWrap}>
          <div style={S.previewLabel}>Vista previa · A4 completo</div>
          <div ref={previewBoxRef} style={S.previewBox}>
            {/* Caja con el tamaño ESCALADO (para centrar bien) + hoja A4 real escalada. */}
            <div style={{ width: 794 * previewScale, height: 1123 * previewScale, flex: "none" }}>
              <div style={{ width: 794, height: 1123, transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
                <PresupuestoExpressTemplate data={templateData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template offscreen para la captura del PDF (tamaño real A4). */}
      <div aria-hidden="true" style={{ position: "absolute", left: -20000, top: 0 }}>
        <PresupuestoExpressTemplate ref={templateRef} data={templateData} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      {children}
    </label>
  );
}

const S = {
  wrap: { padding: 24, maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 20 },
  title: { margin: 0, fontSize: 22, fontWeight: 400, color: colors.gray900 },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: colors.gray600, lineHeight: 1.5, maxWidth: 640 },
  grid: { display: "grid", gridTemplateColumns: "minmax(320px, 420px) 1fr", gap: 24, alignItems: "start" },
  card: { background: colors.white, borderRadius: radius.md, border: `1px solid ${colors.gray200}`, boxShadow: shadows.sm, padding: 18, display: "flex", flexDirection: "column", gap: 10 },
  section: { fontSize: 13, fontWeight: 400, color: colors.primary, marginTop: 6, letterSpacing: "0.02em", textTransform: "uppercase" },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 13, color: colors.gray700 },
  input: { width: "100%", boxSizing: "border-box", height: 42, padding: "0 12px", borderRadius: radius.sm, border: `1px solid ${colors.gray300}`, fontSize: 15, fontFamily: "inherit", outline: "none", background: colors.white, color: colors.gray900 },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  error: { fontSize: 14, color: colors.error, background: colors.errorLight || "#fde8e8", border: `1px solid ${colors.error}`, borderRadius: radius.sm, padding: "8px 10px" },
  btn: { marginTop: 8, height: 46, background: colors.primary, color: colors.white, border: "none", borderRadius: radius.sm, fontSize: 15, fontWeight: 400, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  previewWrap: { display: "flex", flexDirection: "column", gap: 8, position: "sticky", top: 16 },
  previewLabel: { fontSize: 13, color: colors.gray600 },
  previewBox: {
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.md,
    overflow: "hidden",
    boxShadow: shadows.sm,
    background: colors.gray100,
    height: "calc(100vh - 210px)",
    minHeight: 460,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 12,
  },
};
